import { lazy } from 'react';
import {
  createRootRoute, createRoute, createRouter,
  RouterProvider, Outlet, Navigate, redirect,
} from '@tanstack/react-router';
import { LazyBoundary } from './components/LazyBoundary.tsx';
import { Header } from './components/Header.tsx';
import { BottomNav } from './components/BottomNav.tsx';
import { MobileFab } from './components/MobileFab.tsx';
import { Sidebar } from './components/Sidebar.tsx';
import { TopBar } from './components/TopBar.tsx';
import { Login } from './pages/Login.tsx';
import { AcceptInvite } from './pages/AcceptInvite.tsx';
import { ForgotPassword } from './pages/ForgotPassword.tsx';
import { ResetPassword } from './pages/ResetPassword.tsx';
import { SsoHandoff } from './pages/SsoHandoff.tsx';
import { Profile } from './pages/Profile.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import { AdminOrgs } from './pages/AdminOrgs.tsx';
import { AdminOrgDetail } from './pages/AdminOrgDetail.tsx';
import { Inbox } from './pages/Inbox.tsx';
import { MergePdf } from './pages/MergePdf.tsx';
import { CompressPdf } from './pages/CompressPdf.tsx';
import { Settings } from './pages/Settings.tsx';
import { SettingsUsers } from './pages/SettingsUsers.tsx';
import { OrgSettings } from './pages/OrgSettings.tsx';
import { ReviewFlow } from './pages/ReviewFlow.tsx';
import { NotFound } from './pages/NotFound.tsx';
import { useAuthStore } from './store/auth.ts';

// Heavy pages — lazy-loaded to split react-pdf / react-signature-canvas out of the main bundle
const DocumentUpload = lazy(() => import('./pages/DocumentUpload.tsx').then((m) => ({ default: m.DocumentUpload })));
const DocumentBatch = lazy(() => import('./pages/DocumentBatch.tsx').then((m) => ({ default: m.DocumentBatch })));
const DocumentEditor = lazy(() => import('./pages/DocumentEditor.tsx').then((m) => ({ default: m.DocumentEditor })));
const DocumentDetail = lazy(() => import('./pages/DocumentDetail.tsx').then((m) => ({ default: m.DocumentDetail })));
const SignerPage = lazy(() => import('./pages/SignerPage.tsx').then((m) => ({ default: m.SignerPage })));

// Lazy routes are wrapped in LazyBoundary (Suspense + a chunk-load error
// boundary) so a stale-deploy chunk failure reloads/recovers instead of
// blanking the app with a "page loading error".
function Lazy({ children }: { children: React.ReactNode }): JSX.Element {
  return <LazyBoundary>{children}</LazyBoundary>;
}

function RootLayout(): JSX.Element {
  // Sidebar / BottomNav / Header internals already bail when !user, but the
  // <main> element reserves space for the sidebar with md:ml-60. When the user
  // is signed out (Login, AcceptInvite, SignerPage) that offset pushes content
  // right with nothing on the left — so condition the offset on auth state.
  const user = useAuthStore((s) => s.user);
  return (
    <div className={`min-h-screen flex flex-col ${user ? 'bg-[hsl(var(--chrome))]' : ''}`}>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:text-primary-foreground focus:px-3 focus:py-2 focus:text-sm focus:font-medium"
      >
        Skip to main content
      </a>
      <TopBar />
      <Sidebar />
      {/* Fixed inverted-corner mask at the top-left of the content area, so the
          rounded corner stays put as the page scrolls (it's not on the scrolling
          content). Paints only the chrome-coloured notch; content shows through. */}
      {user && (
        <div
          aria-hidden
          className="hidden md:block fixed top-16 left-64 z-30 size-8 pointer-events-none"
          style={{ background: 'radial-gradient(circle at bottom right, transparent 31px, hsl(var(--chrome)) 32px)' }}
        />
      )}
      {/* Mirror notch at the top-right, so the white content panel is rounded on
          both top corners against the chrome. */}
      {user && (
        <div
          aria-hidden
          className="hidden md:block fixed top-16 right-0 z-30 size-8 pointer-events-none"
          style={{ background: 'radial-gradient(circle at bottom left, transparent 31px, hsl(var(--chrome)) 32px)' }}
        />
      )}
      <Header />
      <main
        id="main-content"
        tabIndex={-1}
        className={`flex-1 ${user ? 'has-bottom-nav md:ml-64 md:pt-16' : ''}`}
      >
        {user ? (
          <div className="min-h-dvh bg-background md:min-h-[calc(100dvh-4rem)]">
            <Outlet />
          </div>
        ) : (
          <Outlet />
        )}
      </main>
      <BottomNav />
      <MobileFab />
    </div>
  );
}

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});

const requireAuth = (): void => {
  if (!useAuthStore.getState().accessToken) throw redirect({ to: '/login' });
};

const requireOrgAdmin = (): void => {
  const state = useAuthStore.getState();
  if (!state.accessToken) throw redirect({ to: '/login' });
  if (state.user?.role !== 'ORG_ADMIN') throw redirect({ to: '/dashboard' });
};

const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/login', component: Login });
const inviteRoute = createRoute({ getParentRoute: () => rootRoute, path: '/invite/$token', component: AcceptInvite });
const signRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign/$token',
  component: () => <Lazy><SignerPage /></Lazy>,
});
// Public SSO landing — consumes a one-time handoff token from another Wapplify
// app (e.g. WapMeet) and establishes the session. No auth guard.
const ssoHandoffRoute = createRoute({ getParentRoute: () => rootRoute, path: '/sso/handoff', component: SsoHandoff });
const forgotPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: '/forgot-password', component: ForgotPassword });
const resetPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: '/reset-password/$token', component: ResetPassword });

function RoleAwareIndex(): JSX.Element {
  const role = useAuthStore.getState().user?.role;
  return <Navigate to={role === 'SYS_ADMIN' ? '/admin/orgs' : '/dashboard'} />;
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/', beforeLoad: requireAuth,
  component: RoleAwareIndex,
});
const dashRoute = createRoute({ getParentRoute: () => rootRoute, path: '/dashboard', beforeLoad: requireAuth, component: Dashboard });
const inboxRoute = createRoute({ getParentRoute: () => rootRoute, path: '/inbox', beforeLoad: requireAuth, component: Inbox });
const mergeRoute = createRoute({ getParentRoute: () => rootRoute, path: '/merge', beforeLoad: requireAuth, component: MergePdf });
const compressRoute = createRoute({ getParentRoute: () => rootRoute, path: '/compress', beforeLoad: requireAuth, component: CompressPdf });
// Old /team links keep working — redirect to the Users tab of Settings.
const teamRedirectRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/team',
  beforeLoad: () => { throw redirect({ to: '/settings/users' }); },
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute, path: '/settings',
  beforeLoad: requireOrgAdmin, component: Settings,
});
const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute, path: '/',
  beforeLoad: () => { throw redirect({ to: '/settings/users' }); },
});
const settingsUsersRoute = createRoute({ getParentRoute: () => settingsRoute, path: 'users', component: SettingsUsers });
const settingsReviewRoute = createRoute({ getParentRoute: () => settingsRoute, path: 'review', component: ReviewFlow });
const settingsOrgRoute = createRoute({ getParentRoute: () => settingsRoute, path: 'org', component: OrgSettings });
const profileRoute = createRoute({ getParentRoute: () => rootRoute, path: '/profile', beforeLoad: requireAuth, component: Profile });
const uploadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents/new',
  beforeLoad: requireAuth,
  component: () => <Lazy><DocumentUpload /></Lazy>,
});
const batchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents/batch',
  beforeLoad: requireAuth,
  component: () => <Lazy><DocumentBatch /></Lazy>,
});
const editRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents/$id/edit',
  beforeLoad: requireAuth,
  component: () => <Lazy><DocumentEditor /></Lazy>,
});
const docRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/documents/$id',
  beforeLoad: requireAuth,
  component: () => <Lazy><DocumentDetail /></Lazy>,
});

const adminOrgsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/admin/orgs', beforeLoad: requireAuth, component: AdminOrgs });
const adminOrgDetailRoute = createRoute({ getParentRoute: () => rootRoute, path: '/admin/orgs/$id', beforeLoad: requireAuth, component: AdminOrgDetail });

const routeTree = rootRoute.addChildren([
  loginRoute, inviteRoute, signRoute, ssoHandoffRoute, forgotPasswordRoute, resetPasswordRoute,
  indexRoute, dashRoute, inboxRoute, mergeRoute, compressRoute, teamRedirectRoute, profileRoute,
  settingsRoute.addChildren([settingsIndexRoute, settingsUsersRoute, settingsReviewRoute, settingsOrgRoute]),
  uploadRoute, batchRoute, editRoute, docRoute,
  adminOrgsRoute, adminOrgDetailRoute,
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register { router: typeof router }
}

export function AppRouter(): JSX.Element { return <RouterProvider router={router} />; }
